import React, { useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList
} from 'recharts';
import { IconButton } from '@mui/material';
import { PrintOutlined } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import Loading from 'components/Loading';
import './AppDataTable.css';
import SentryWrap from 'components/Sentry';

const SkillReport = ({overallReport}) => {
  const printRef = useRef(null);
  const [printLoader, setPrintLoader] = useState(false);
  const PRINT_CHART_WIDTH = 760;
  const PRINT_CHART_HEIGHT = 560;

  const printCall = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: letter landscape;
        margin: 6mm;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
    onBeforeGetContent: async () => {
      setPrintLoader(true);
      if (printRef.current) {
        printRef.current.style.height = 'auto';
        printRef.current.style.overflow = 'visible';
      }
      await new Promise((resolve) => setTimeout(resolve, 120));
      window.dispatchEvent(new Event('resize'));
      await new Promise((resolve) => setTimeout(resolve, 100));
    },
    onAfterPrint: async () => {
      setPrintLoader(false);
      if (printRef.current) {
        printRef.current.style.height = '';
        printRef.current.style.overflow = '';
      }
    },
  });

  const calculateScaleReport = (categoryData) => {
    if (!categoryData || !categoryData.score) return 0;
    
    const proficiencyWeights = {
      'Novice': 1,
      'Early_Intermediate': 2,
      'Intermediate': 3,
      'Advance': 4,
      'Advance_High': 5
    };

    let total = 0;
    Object.keys(proficiencyWeights).forEach(level => {
      const count = categoryData.score[level] || 0;
      total += count * proficiencyWeights[level];
    });

    return total;
  };

  const calculatePercentage = (count, total) => {
    if (total === 0) return 0;
    return ((count / total) * 100).toFixed(1);
  };

  const data = useMemo(() => {
    if (!overallReport) return [];

    const categories = [
      { key: 'speaking', label: 'Speaking' },
      { key: 'listening', label: 'Listening' },
      { key: 'reading', label: 'Reading' },
      { key: 'writing', label: 'Writing' },
      { key: 'comprehension', label: 'Comprehension', isCombined: true, keys: ['comprehension'] },
      { key: 'literacy', label: 'Literacy', isCombined: true, keys: ['literacy'] },
      { key: 'oral', label: 'Vocal', isCombined: true, keys: ['oral'] },
      { key: 'productive', label: 'Active', isCombined: true, keys: ['productive'] }
    ];

    return categories.map((category, index) => {
      let categoryData = null;
      let scaleReportValue = 0;

      if (category.isCombined && category.keys) {
        const combinedScore = {
          Novice: 0,
          Early_Intermediate: 0,
          Intermediate: 0,
          Advance: 0,
          Advance_High: 0
        };

        category.keys.forEach(key => {
          const data = overallReport[key];
          if (data && data.score) {
            Object.keys(combinedScore).forEach(level => {
              combinedScore[level] += (data.score[level] || 0);
            });
          }
        });

        categoryData = { score: combinedScore };
        scaleReportValue = overallReport.scale_report && overallReport.scale_report[category.key] 
          ? overallReport.scale_report[category.key] 
          : calculateScaleReport(categoryData);
      } else {
        categoryData = overallReport[category.key];
        if (categoryData) {
          scaleReportValue = overallReport.scale_report && overallReport.scale_report[category.key] 
            ? overallReport.scale_report[category.key] 
            : calculateScaleReport(categoryData);
        }
      }

      if (!categoryData || !categoryData.score) {
        return {
          id: index + 1,
          category: category.label,
          numberTested: 0,
          number_of_submissions: 0,
          novice: 0,
          early_intermediate: 0,
          intermediate: 0,
          advance: 0,
          advance_high: 0,
          scaleReport: 0
        };
      }

      const score = categoryData.score;
      const totalPoints = (score.Novice || 0) + 
                         (score.Early_Intermediate || 0) + 
                         (score.Intermediate || 0) + 
                         (score.Advance || 0) + 
                         (score.Advance_High || 0);
      
      const novice = parseFloat(calculatePercentage(score.Novice || 0, totalPoints));
      const early_intermediate = parseFloat(calculatePercentage(score.Early_Intermediate || 0, totalPoints));
      const intermediate = parseFloat(calculatePercentage(score.Intermediate || 0, totalPoints));
      const advance = parseFloat(calculatePercentage(score.Advance || 0, totalPoints));
      const advance_high = parseFloat(calculatePercentage(score.Advance_High || 0, totalPoints));
      
      const allPercentagesZero = novice === 0 && early_intermediate === 0 && intermediate === 0 && advance === 0 && advance_high === 0;
      
      const number_of_submissions = allPercentagesZero
        ? 0
        : (overallReport?.number_of_submissions || 0);

      return {
        id: index + 1,
        category: category.label,
        numberTested: number_of_submissions,
        number_of_submissions: number_of_submissions,
        novice: novice,
        early_intermediate: early_intermediate,
        intermediate: intermediate,
        advance: advance,
        advance_high: advance_high,
        scaleReport: scaleReportValue
      };
    });
  }, [overallReport]);

  const legendPayload = [
    { value: 'Novice', type: 'rect', id: 'novice', color: '#5B9BD5' },
    { value: 'Early Intermediate', type: 'rect', id: 'early_intermediate', color: '#FFB366' },
    { value: 'Intermediate', type: 'rect', id: 'intermediate', color: '#6BB6FF' },
    { value: 'Advance', type: 'rect', id: 'advance', color: '#B8A9D9' },
    { value: 'Advance High', type: 'rect', id: 'advance_high', color: '#70AD47' }
  ];

  const createLabelRenderer = (dataKey) => (props) => {
    const { x, y, width, height, payload, value } = props;
    const actualValue = value !== undefined ? value : (payload && payload[dataKey] ? payload[dataKey] : 0);
    
    if (actualValue > 0 && width > 25) {
      return (
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="6"
          fontWeight="500"
        >
          {actualValue.toFixed(1)}%
        </text>
      );
    }
    return null;
  };

  const renderLegend = () => {
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 12px' }}>
        {legendPayload.map((entry, index) => (
          <li key={`item-${index}`} style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', margin: 0 }}>
            <svg width="14" height="14" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
              <rect width="14" height="14" fill={entry.color} />
            </svg>
            <span style={{ fontSize: '12px' }}>{entry.value}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const firstPayload = payload.find(p => p.payload) || payload[0];
    const data = firstPayload?.payload;
    
    if (!data || !data.category) return null;

    const proficiencyLevels = [
      { key: 'advance', label: 'Advance', value: data.advance || 0, color: '#B8A9D9' },
      { key: 'advance_high', label: 'Advance High', value: data.advance_high || 0, color: '#70AD47' },
      { key: 'early_intermediate', label: 'Early Intermediate', value: data.early_intermediate || 0, color: '#FFB366' },
      { key: 'intermediate', label: 'Intermediate', value: data.intermediate || 0, color: '#6BB6FF' },
      { key: 'novice', label: 'Novice', value: data.novice || 0, color: '#5B9BD5' }
    ];

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        minWidth: '200px'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
          {data.category}
        </div>
        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
          Number tested: {data.numberTested ?? 0}
        </div>
        {proficiencyLevels.map((level) => (
          <div key={level.key} style={{ 
            marginBottom: '4px', 
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ color: level.color, marginRight: '8px', minWidth: '140px' }}>
              {level.label} : {level.value.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderGroupAndTestedTick = ({ x, y, payload }) => {
    const row = data.find((item) => item.category === payload?.value);
    const category = row?.category ?? payload?.value ?? '';
    const tested = row?.numberTested ?? 0;
    const GROUP_COL_WIDTH = 95;
    const TESTED_COL_WIDTH = 68;

    // Keep column order fixed: Group (left), Number Tested (middle), bars (right).
    return (
      <g>
        <text
          x={x - (GROUP_COL_WIDTH + TESTED_COL_WIDTH) + 4}
          y={y}
          dy="0.355em"
          fill="#666"
          fontSize="12"
          textAnchor="start"
        >
          {category}
        </text>
        <text
          x={x - 8}
          y={y}
          dy="0.355em"
          fill="#666"
          fontSize="12"
          textAnchor="end"
        >
          {tested}
        </text>
      </g>
    );
  };

  const renderChartContent = () => (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        type="number"
        domain={[0, 100]}
        tick={{ fontSize: 12 }}
        tickFormatter={(value) => `${value}`}
      />
      <YAxis
        yAxisId="group"
        type="category"
        dataKey="category"
        width={170}
        tick={renderGroupAndTestedTick}
        axisLine={true}
        tickLine={false}
        padding={{ top: 0, bottom: 0 }}
      />
      <Tooltip
        content={renderCustomTooltip}
        cursor={{ fill: 'rgba(0,0,0,0.06)' }}
      />
      <Legend
        wrapperStyle={{ fontSize: '11px', paddingTop: 8 }}
        iconType="rect"
        payload={legendPayload}
        content={renderLegend}
      />
      <Bar
        dataKey="novice"
        yAxisId="group"
        stackId="a"
        fill="#5B9BD5"
        name="Novice"
      >
        <LabelList dataKey="novice" content={createLabelRenderer('novice')} />
      </Bar>
      <Bar
        dataKey="early_intermediate"
        yAxisId="group"
        stackId="a"
        fill="#FFB366"
        name="Early Intermediate"
      >
        <LabelList dataKey="early_intermediate" content={createLabelRenderer('early_intermediate')} />
      </Bar>
      <Bar
        dataKey="intermediate"
        yAxisId="group"
        stackId="a"
        fill="#6BB6FF"
        name="Intermediate"
      >
        <LabelList dataKey="intermediate" content={createLabelRenderer('intermediate')} />
      </Bar>
      <Bar
        dataKey="advance"
        yAxisId="group"
        stackId="a"
        fill="#B8A9D9"
        name="Advance"
      >
        <LabelList dataKey="advance" content={createLabelRenderer('advance')} />
      </Bar>
      <Bar
        dataKey="advance_high"
        yAxisId="group"
        stackId="a"
        fill="#70AD47"
        name="Advance High"
      >
        <LabelList dataKey="advance_high" content={createLabelRenderer('advance_high')} />
      </Bar>
    </>
  );

  if (!overallReport || !data || data.length === 0) {
    return (
      <div className="skill-proficiency-chart-wrapper">
        <h2 className="skill-report-title">Skill Report</h2>
        <div>No data available</div>
      </div>
    );
  }

  return (
    <SentryWrap>
      <div className="skill-proficiency-chart-wrapper printable" ref={printRef}>
      <div className="skill-report-header-container">
        <h2 className="skill-report-title">Skill Report</h2>
        <div className="skill-report-print-button-container">
          <IconButton
            onClick={() => printCall()}
            className="skill-report-print-button"
            aria-label="print"
          >
            {printLoader ? <Loading size={20} /> : <PrintOutlined />}
          </IconButton>
        </div>
      </div>
      <div className="skill-report-header-row">
        <div className="skill-report-header-group">Group</div>
        <div className="skill-report-header-number">Number Tested</div>
        <div className="skill-report-header-percent">% of Students</div>
      </div>
      <div className="skill-report-chart-container" style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div className="skill-report-chart-area" style={{ flex: 1, marginLeft: 0, paddingLeft: 0, paddingTop: 0 }}>
          <div className="skill-chart-print-only">
            <BarChart
              width={PRINT_CHART_WIDTH}
              height={PRINT_CHART_HEIGHT}
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 26, left: 0, bottom: 92 }}
              barCategoryGap="10%"
            >
              {renderChartContent()}
            </BarChart>
          </div>
          <div className="skill-chart-screen-only">
            <ResponsiveContainer width="100%" height={500} className="skill-chart-responsive">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 0, right: 10, left: 0, bottom: 50 }}
                barCategoryGap="10%"
              >
                {renderChartContent()}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="skill-report-print-legend">
        {legendPayload.map((entry) => (
          <span key={entry.id} className="skill-report-print-legend-item">
            <span
              className="skill-report-print-legend-swatch"
              style={{ backgroundColor: entry.color }}
            />
            <span className="skill-report-print-legend-text">{entry.value}</span>
          </span>
        ))}
      </div>
      </div>
    </SentryWrap>
  );
};

export default SkillReport;

